/* eslint-disable replo/no-tailwind-literal-color */
import React, { useCallback, useMemo, useState } from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Checkbox } from "@replo/design-system/components/checkbox/Checkbox";
import { Progress } from "@replo/design-system/components/progress/Progress";
import {
  RadioGroup,
  RadioGroupItem,
} from "@replo/design-system/components/radio-group/RadioGroup";
import twMerge from "@replo/design-system/utils/twMerge";
import { ChevronLeft } from "lucide-react";
import { motion } from "motion/react";

// NOTE (Yuxin, 2026-02-04): Multi-step question flow component for agent questions.
// Supports single/multi-select per step with progress bar and navigation.
// Uses minimal inline styling to match QuestionToolBlock design.

interface QuestionFlowOption {
  id: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface QuestionFlowStep {
  id: string;
  title: string;
  description?: string;
  options: QuestionFlowOption[];
  selectionMode?: "single" | "multi";
}

interface QuestionFlowProps {
  id: string;
  steps: QuestionFlowStep[];
  onComplete: (answers: Record<string, string[]>) => void;
  onStepChange?: (stepId: string) => void;
  className?: string;
}

export function QuestionFlow({
  id,
  steps,
  onComplete,
  onStepChange,
  className,
}: QuestionFlowProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});

  const totalSteps = steps.length;
  const isLastStep = currentStepIndex === totalSteps - 1;

  // Get current step with bounds check
  const currentStep = steps[currentStepIndex];

  const currentSelection = useMemo(() => {
    if (!currentStep) {
      return new Set<string>();
    }
    const answer = answers[currentStep.id];
    return new Set(answer ?? []);
  }, [answers, currentStep]);

  const canProceed = currentSelection.size > 0;
  const selectionMode = currentStep?.selectionMode ?? "single";

  const handleToggle = useCallback(
    (optionLabel: string) => {
      if (!currentStep) {
        return;
      }
      const mode = currentStep.selectionMode ?? "single";
      setAnswers((prev) => {
        const current = prev[currentStep.id] ?? [];
        let next: string[];

        if (mode === "single") {
          next = current.includes(optionLabel) ? [] : [optionLabel];
        } else {
          next = current.includes(optionLabel)
            ? current.filter((label) => label !== optionLabel)
            : [...current, optionLabel];
        }

        return { ...prev, [currentStep.id]: next };
      });
    },
    [currentStep],
  );

  const handleBack = useCallback(() => {
    if (currentStepIndex > 0) {
      const prevIndex = currentStepIndex - 1;
      const prevStep = steps[prevIndex];
      setCurrentStepIndex(prevIndex);
      if (prevStep) {
        onStepChange?.(prevStep.id);
      }
    }
  }, [currentStepIndex, onStepChange, steps]);

  const handleNext = useCallback(() => {
    if (currentSelection.size === 0) {
      return;
    }

    if (isLastStep) {
      onComplete(answers);
    } else {
      const nextIndex = currentStepIndex + 1;
      const nextStep = steps[nextIndex];
      setCurrentStepIndex(nextIndex);
      if (nextStep) {
        onStepChange?.(nextStep.id);
      }
    }
  }, [
    answers,
    currentSelection.size,
    currentStepIndex,
    isLastStep,
    onComplete,
    onStepChange,
    steps,
  ]);

  // Early return if no valid step
  if (!currentStep) {
    return null;
  }

  const stepLabel = `Step ${currentStepIndex + 1} of ${totalSteps}`;

  return (
    <motion.div
      className={twMerge("flex w-full flex-col gap-3", className)}
      data-tool-ui-id={id}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Progress */}
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {stepLabel}
        </span>
        <Progress
          value={currentStepIndex + 1}
          max={totalSteps}
          className="h-1"
          UNSAFE_indicatorClassName="bg-neutral"
        />
      </div>

      {/* Question header and text */}
      <div className="flex flex-col gap-1">
        {currentStep.description && (
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {currentStep.description}
          </span>
        )}
        <span className="text-sm font-medium">{currentStep.title}</span>
      </div>

      {/* Options */}
      {selectionMode === "single" ? (
        <RadioGroup
          value={Array.from(currentSelection)[0] ?? ""}
          onValueChange={(value) => handleToggle(value as string)}
          UNSAFE_className="gap-0.5"
        >
          {currentStep.options.map((option) => (
            <label
              key={option.id}
              className={twMerge(
                "group flex w-full items-start gap-2.5 py-1.5 px-1 rounded text-left transition-colors cursor-pointer",
                option.disabled && "opacity-50 cursor-not-allowed",
              )}
            >
              <div className="flex-shrink-0 mt-0.5">
                <RadioGroupItem
                  value={option.label}
                  disabled={option.disabled}
                />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-sm text-slate-700 group-hover:text-black transition-colors">
                  {option.label}
                </span>
                {option.description && (
                  <span className="text-sm text-slate-500">
                    {option.description}
                  </span>
                )}
              </div>
            </label>
          ))}
        </RadioGroup>
      ) : (
        <div className="flex flex-col gap-0.5">
          {currentStep.options.map((option) => (
            <label
              key={option.id}
              className={twMerge(
                "group flex w-full items-start gap-2.5 py-1.5 px-1 rounded text-left transition-colors cursor-pointer",
                option.disabled && "opacity-50 cursor-not-allowed",
              )}
            >
              <div className="flex-shrink-0 mt-0.5">
                <Checkbox
                  checked={currentSelection.has(option.label)}
                  onCheckedChange={() => handleToggle(option.label)}
                  disabled={option.disabled}
                />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-sm text-slate-700 group-hover:text-black transition-colors">
                  {option.label}
                </span>
                {option.description && (
                  <span className="text-sm text-slate-500">
                    {option.description}
                  </span>
                )}
              </div>
            </label>
          ))}
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-1">
        {currentStepIndex > 0 ? (
          <Button
            variant="tertiary"
            size="base"
            onClick={handleBack}
            startEnhancer={<ChevronLeft className="size-4" />}
          >
            Back
          </Button>
        ) : (
          <div />
        )}
        <Button
          variant="primary"
          size="base"
          onClick={handleNext}
          disabled={!canProceed}
        >
          {isLastStep ? "Complete" : "Next Question"}
        </Button>
      </div>
    </motion.div>
  );
}
